import React, { useMemo } from "react";
import { FormHelperText } from "@mui/material";
import { useTranslation } from "react-i18next";
import Dropdown from "components/Elements/Dropdown";
import AddDialogStyle from "styles/application/AddDialogStyle";
import useUserLocalePreference from "hooks/useUserLocalePreference";

const ADD = AddDialogStyle();

const languageAutonym = (tag) => {
	try {
		const name = new Intl.DisplayNames([tag], { type: "language" }).of(tag);
		return name && name !== tag ? name : tag;
	} catch {
		return tag;
	}
};

const localeLabel = (tag) => {
	const name = languageAutonym(tag);
	return name === tag ? tag : `${name} (${tag})`;
};

export const LocalePreferenceSelect = ({
	value,
	onChange,
	available,
	siteDefault,
	disabled = false,
	helperText,
}) => {
	const { t } = useTranslation();

	const options = useMemo(() => {
		const tags = available.includes(siteDefault)
			? available
			: [siteDefault, ...available];
		return tags.map((tag) => ({ label: localeLabel(tag), value: tag }));
	}, [available, siteDefault]);

	// A preference that is unset - or no longer among the enabled locales -
	// resolves to the site default at runtime, so display that option as
	// selected. Every selection saves explicitly, so choosing the default's
	// language pins it rather than following future default changes.
	const effectiveValue = options.some((option) => option.value === value)
		? value
		: siteDefault;
	const selected =
		options.find((option) => option.value === effectiveValue) || {};

	return (
		<>
			<ADD.InputLabel>{t("label.preferredLanguage")}</ADD.InputLabel>
			<Dropdown
				options={options}
				selectedValue={selected}
				onChange={(option) => onChange(option.value)}
				disabled={disabled}
				width="100%"
			/>
			{helperText && <FormHelperText>{helperText}</FormHelperText>}
		</>
	);
};

const UserLanguagePreference = ({ userId, disabled = false } = {}) => {
	const { t } = useTranslation();
	const { enabled, available, siteDefault, selected, busy, save } =
		useUserLocalePreference({ targetUserId: userId });

	if (!enabled) return null;

	return (
		<LocalePreferenceSelect
			value={selected}
			onChange={save}
			available={available}
			siteDefault={siteDefault}
			disabled={busy || disabled}
			helperText={t("message.languagePreferenceHelper")}
		/>
	);
};

export default UserLanguagePreference;
